import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Form, Input, Button, Avatar, message, Space, Row, Col, Statistic, List, Modal, Badge } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  EditOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  ShareAltOutlined,
  TeamOutlined,
  RightOutlined,
  CameraOutlined,
  BarChartOutlined,
  LogoutOutlined,
  FileProtectOutlined,
  CrownOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import useConfigStore from '../stores/configStore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { TextArea } = Input;

const Profile = () => {
  const { user, logout, updateProfile, changePassword, verifyToken, initialize, updateUser } = useAuthStore();
  const { membershipEnabled } = useConfigStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [msgUnread, setMsgUnread] = useState(0);

  // 私信未读数（进入页面 / 切回前台时刷新）
  useEffect(() => {
    const fetchUnread = () => {
      axios.get('/messages/unread-count')
        .then((r) => { if (r.data?.success) setMsgUnread(r.data.unread || 0); })
        .catch(() => {});
    };
    fetchUnread();
    window.addEventListener('focus', fetchUnread);
    document.addEventListener('visibilitychange', fetchUnread);
    return () => {
      window.removeEventListener('focus', fetchUnread);
      document.removeEventListener('visibilitychange', fetchUnread);
    };
  }, []);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [statistics, setStatistics] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [shareLoading, setShareLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState(user?.avatar);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarPreviewOpen, setAvatarPreviewOpen] = useState(false);
  const [isTsAgent, setIsTsAgent] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => { setCurrentAvatar(user?.avatar); }, [user?.avatar]);

  // 上传/更换头像（原生 file input，兼容移动 WebView）
  const doUploadAvatar = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { message.error('请选择图片文件'); return; }
    setAvatarUploading(true);
    const fd = new FormData();
    fd.append('avatar', file);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/auth/avatar', fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setCurrentAvatar(res.data.avatar);
        if (updateUser) updateUser({ avatar: res.data.avatar });
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          stored.avatar = res.data.avatar;
          localStorage.setItem('user', JSON.stringify(stored));
        } catch (_) {}
        message.success('头像已更新');
      } else {
        message.error(res.data.message || '更新失败');
      }
    } catch (e) {
      message.error(e.response?.data?.message || '头像上传失败');
    } finally {
      setAvatarUploading(false);
    }
  };

  const onAvatarInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    doUploadAvatar(file);
  };

  // 触发「更换头像」（小程序走原生页，其余走原生 input）
  const triggerChangeAvatar = () => {
    // 只有真正在小程序 web-view 里 __wxjs_environment 才等于 'miniprogram'；
    // 注意：普通浏览器加载 jweixin 后 window.wx.miniProgram.navigateTo 也存在（空操作），
    // 不能用它判断环境，否则网页上会误走小程序分支导致「点了没反应」。
    const inMini = typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram';
    if (inMini && window.wx && window.wx.miniProgram && window.wx.miniProgram.navigateTo) {
      const token = localStorage.getItem('token') || '';
      window.wx.miniProgram.navigateTo({ url: '/pages/avatar/avatar?token=' + encodeURIComponent(token) });
      return;
    }
    if (!avatarUploading && avatarInputRef.current) avatarInputRef.current.click();
  };

  const detectMiniProgramEnv = useCallback(() => {
    return new Promise((resolve) => {
      try {
        if (window.wx && window.wx.miniProgram && typeof window.wx.miniProgram.getEnv === 'function') {
          window.wx.miniProgram.getEnv((res) => {
            resolve(!!res?.miniprogram);
          });
          return;
        }
      } catch (_) {}
      resolve(window.__wxjs_environment === 'miniprogram');
    });
  }, []);

  const handleMiniProgramShare = useCallback(async () => {
    if (shareLoading) return;
    setShareLoading(true);

    try {
      const inMiniProgram = await detectMiniProgramEnv();

      if (inMiniProgram && window.wx?.miniProgram) {
        if (typeof window.wx.miniProgram.navigateTo === 'function') {
          window.wx.miniProgram.navigateTo({ url: '/pages/referral/referral?source=portalShare' });
          message.success('正在打开分享页');
        } else if (typeof window.wx.miniProgram.postMessage === 'function') {
          window.wx.miniProgram.postMessage({ data: { action: 'shareReferral' } });
          message.success('正在打开分享页');
        } else {
          message.warning('当前环境暂不支持分享，请稍后重试');
        }
      } else {
        message.info('请在小程序内使用“一键分享”功能');
      }
    } catch (error) {
      console.error('触发小程序分享失败:', error);
      message.error('暂时无法发起分享，请稍后重试');
    } finally {
      setTimeout(() => setShareLoading(false), 300);
    }
  }, [detectMiniProgramEnv, shareLoading]);

  const openReferralStats = useCallback(() => {
    navigate('/referral');
  }, [navigate]);


  // 调试信息
  console.log('Profile页面 - 用户信息:', user);
  console.log('Profile页面 - 用户角色:', user?.role);
  console.log('Profile页面 - 会员等级:', user?.member_level);
  console.log('Profile页面 - 试用期结束时间:', user?.trial_end_date);
  console.log('Profile页面 - 是否显示会员管理按钮:', user?.role === 'admin');
  console.log('Profile页面 - localStorage中的token:', localStorage.getItem('token') ? '存在' : '不存在');
  console.log('Profile页面 - axios默认headers:', axios.defaults.headers.common['Authorization']);

  // 获取统计数据（P0：统一仪表盘 API，单次请求替代 20 路并发）
  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      await initialize();
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('/platform/dashboard', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.dashboard?.moduleStats) {
        const stats = {};
        response.data.dashboard.moduleStats.forEach((item) => {
          stats[item.key] = {
            name: item.name,
            icon: <span style={{ fontSize: 16 }}>{item.icon}</span>,
            count: item.count,
          };
        });
        setStatistics(stats);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // 从小程序原生「换头像」页返回后，网页重新可见时刷新用户信息（拿到最新头像）
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        const refresh = useAuthStore.getState().refreshUser;
        if (refresh) refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, []);

  // 组件加载时验证token并获取统计数据
  useEffect(() => {
    initialize();
    verifyToken();
    fetchStatistics();
    // 判断当前用户是否为 TS 代理人，仅代理人显示「TS 代理平台」入口
    axios.get('/ts/portal/is-agent')
      .then((r) => setIsTsAgent(!!r.data?.isAgent))
      .catch(() => setIsTsAgent(false));
  }, [initialize, verifyToken]);



  // 发送验证码
  const handleSendCode = async () => {
    try {
      const phone = profileForm.getFieldValue('phone');
      console.log('准备发送验证码，手机号:', phone);
      
      if (!phone) {
        message.error('请先输入手机号');
        return;
      }
      
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        message.error('请输入正确的手机号格式');
        return;
      }
      
      setSendingCode(true);
      console.log('开始发送验证码请求...');
      
      const response = await axios.post('/auth/sms/send-code', {
        phone,
        purpose: 'bind'
      });
      
      console.log('验证码发送响应:', response.data);
      
      if (response.data.success) {
        message.success(response.data.testMode ? 
          `验证码已发送（测试模式，验证码：${response.data.testCode || '123456'}）` : 
          '验证码已发送');
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        message.error(response.data.message || '发送失败');
      }
    } catch (error) {
      console.error('发送验证码失败，详细错误:', error);
      console.error('错误响应:', error.response);
      const errorMsg = error.response?.data?.message || error.message || '发送验证码失败';
      message.error(errorMsg);
    } finally {
      setSendingCode(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const values = await profileForm.validateFields();
      setLoading(true);
      
      // 确保token已设置到axios headers
      const token = localStorage.getItem('token');
      console.log('准备更新个人信息，token:', token ? '存在' : '不存在');
      console.log('token值:', token);
      
      if (!token) {
        message.error('登录状态已失效，请重新登录');
        setLoading(false);
        return;
      }
      
      // 直接使用axios发送请求，不通过authStore
      console.log('准备发送请求到:', axios.defaults.baseURL + '/auth/profile');
      console.log('请求数据:', values);
      
      const response = await axios.put('/auth/profile', values, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('收到响应:', response.data);
      const result = response.data;
      
      if (result.success) {
        message.success(result.message || '个人信息更新成功');
        setEditOpen(false);
        
        // 如果账户合并了，需要更新token
        if (result.merged && result.token) {
          console.log('账户已合并，更新token');
          localStorage.setItem('token', result.token);
          localStorage.setItem('user', JSON.stringify(result.user));
          axios.defaults.headers.common['Authorization'] = `Bearer ${result.token}`;
          // 刷新页面以加载新的用户数据
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error('更新个人信息失败:', error);
      message.error(error.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        message.error('两次输入的密码不一致');
        return;
      }
      
      setLoading(true);
      const result = await changePassword(values.currentPassword, values.newPassword);
      
      if (result.success) {
        message.success('密码修改成功');
        setPwdOpen(false);
        passwordForm.resetFields();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('密码修改失败');
    } finally {
      setLoading(false);
    }
  };

  const validPhone = (user?.phone && !user?.phone.startsWith('wx_') && !user?.phone.startsWith('wechat_user_') && /^1[3-9]\d{9}$/.test(user?.phone)) ? user.phone : '';

  const menuItem = (icon, title, onClick, danger) => (
    <List.Item
      onClick={onClick}
      style={{ cursor: 'pointer', padding: '13px 16px' }}
      actions={[<RightOutlined key="a" style={{ color: '#c5c8ce', fontSize: 12 }} />]}
    >
      <List.Item.Meta
        avatar={<span style={{ fontSize: 17, color: danger ? '#ff4d4f' : '#6366F1' }}>{icon}</span>}
        title={<span style={{ fontSize: 14, color: danger ? '#ff4d4f' : '#1f2430' }}>{title}</span>}
      />
    </List.Item>
  );

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '12px 12px 28px' }}>
      {/* 头部：可换头像 + 信息 */}
      <Card style={{ borderRadius: 16, marginBottom: 12 }} bodyStyle={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ position: 'relative', lineHeight: 0 }}>
            {/* 点头像看大图（原图）；无头像则去更换 */}
            <span
              onClick={() => { if (currentAvatar) setAvatarPreviewOpen(true); else triggerChangeAvatar(); }}
              style={{ cursor: 'pointer' }}
            >
              <Avatar size={72} src={currentAvatar} icon={<UserOutlined />} />
            </span>
            {/* 右下角相机角标：更换头像 */}
            <div
              onClick={(e) => { e.stopPropagation(); triggerChangeAvatar(); }}
              style={{ position: 'absolute', right: -2, bottom: -2, width: 26, height: 26, borderRadius: '50%', background: '#6366F1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', cursor: 'pointer', zIndex: 2 }}
            >
              {avatarUploading ? <ReloadOutlined spin style={{ fontSize: 12 }} /> : <CameraOutlined style={{ fontSize: 13 }} />}
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onAvatarInputChange}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2430' }}>{user?.nickname || '未命名'}</div>
            <div style={{ color: '#8c8c8c', fontSize: 13, marginTop: 2 }}>{validPhone || '未绑定手机号'}</div>
            <div style={{ marginTop: 8 }}>
              <span style={{ background: '#f0f0ff', color: '#6366F1', fontSize: 12, padding: '2px 10px', borderRadius: 10 }}>
                {user?.role === 'admin' ? '管理员' : (user?.member_level || '普通用户')}
              </span>
              {user?.trial_end_date && user?.role !== 'admin' && (
                <span style={{ fontSize: 11, color: '#1890ff', marginLeft: 8 }}>有效期至 {new Date(user.trial_end_date).toLocaleDateString()}</span>
              )}
            </div>
          </div>
          <a onClick={() => setEditOpen(true)} style={{ whiteSpace: 'nowrap' }}><EditOutlined /> 编辑</a>
        </div>
      </Card>

      {/* 常用功能 */}
      <Card size="small" style={{ borderRadius: 16, marginBottom: 12 }} bodyStyle={{ padding: '2px 0' }}>
        <List>
          {menuItem(<ShareAltOutlined />, '一键分享', handleMiniProgramShare)}
          {menuItem(<TeamOutlined />, '我的分享', openReferralStats)}
          {menuItem(<BarChartOutlined />, '模块数据统计', () => { fetchStatistics(); setStatsOpen(true); })}
        </List>
      </Card>

      {/* 会员服务（全免费模式下对普通用户隐藏；管理员仅保留「管理后台」入口，方便切回开关）*/}
      {(membershipEnabled || user?.role === 'admin') && (
        <Card size="small" title={membershipEnabled ? '会员服务' : '管理'} style={{ borderRadius: 16, marginBottom: 12 }} bodyStyle={{ padding: '2px 0' }}>
          <List>
            {membershipEnabled && menuItem(<CreditCardOutlined />, '购买会员卡', () => navigate('/card-sales'))}
            {membershipEnabled && isTsAgent && menuItem(<TeamOutlined />, 'TS 代理平台', () => navigate('/ts-agent'))}
            {membershipEnabled && isTsAgent && menuItem(
              <Badge count={msgUnread} size="small" offset={[6, -2]}><MessageOutlined /></Badge>,
              '我的私信（上下级）',
              () => navigate('/messages')
            )}
            {membershipEnabled && menuItem(<ShoppingOutlined />, '我的订单', () => navigate('/my-orders'))}
            {user?.role === 'admin' && menuItem(<CrownOutlined style={{ color: '#faad14' }} />, '管理后台', () => navigate('/admin'))}
          </List>
          {membershipEnabled && <div style={{ padding: '0 16px 10px', color: '#bbb', fontSize: 12 }}>普通用户可免费使用一个月，升级 VIP 可用全部模块一年</div>}
        </Card>
      )}

      {/* 账号与安全 */}
      <Card size="small" title="账号与安全" style={{ borderRadius: 16, marginBottom: 12 }} bodyStyle={{ padding: '2px 0' }}>
        <List>
          {menuItem(<LockOutlined />, '修改密码', () => setPwdOpen(true))}
          {menuItem(<FileProtectOutlined />, '隐私政策', () => navigate('/privacy'))}
          {menuItem(<FileProtectOutlined />, '用户协议', () => navigate('/terms'))}
          {menuItem(<UserOutlined />, '账号注销', () => navigate('/delete-account'), true)}
        </List>
      </Card>

      <Button block danger icon={<LogoutOutlined />} onClick={logout} style={{ borderRadius: 12, height: 44 }}>退出登录</Button>

      {/* 头像大图预览 + 更换入口 */}
      <Modal open={avatarPreviewOpen} onCancel={() => setAvatarPreviewOpen(false)} footer={null} title={null} centered styles={{ body: { padding: 16, textAlign: 'center' } }}>
        {currentAvatar && (
          <img src={currentAvatar} alt="头像" style={{ maxWidth: '100%', maxHeight: '68vh', borderRadius: 8, display: 'block', margin: '0 auto' }} />
        )}
        <Button type="primary" icon={<CameraOutlined />} style={{ marginTop: 16 }} loading={avatarUploading}
          onClick={() => { setAvatarPreviewOpen(false); triggerChangeAvatar(); }}>
          更换头像
        </Button>
      </Modal>

      {/* 编辑资料 */}
      <Modal title="编辑资料" open={editOpen} onCancel={() => setEditOpen(false)} onOk={handleUpdateProfile} okText="保存" confirmLoading={loading} destroyOnClose>
        <Form form={profileForm} layout="vertical" initialValues={{ ...user, phone: validPhone }}>
          <Form.Item name="nickname" label="昵称" rules={[{ required: true, message: '请输入昵称' }, { min: 2, message: '昵称至少2个字符' }]}>
            <Input prefix={<UserOutlined />} placeholder="请输入昵称" />
          </Form.Item>
          <Form.Item name="phone" label="手机号">
            <Input placeholder="绑定/修改手机号" />
          </Form.Item>
          <Form.Item name="sms_code" label="短信验证码" tooltip="修改手机号时需要">
            <Input placeholder="仅修改手机号时填写" addonAfter={
              <Button type="link" size="small" style={{ padding: 0 }} onClick={handleSendCode} loading={sendingCode} disabled={countdown > 0}>
                {countdown > 0 ? `${countdown}s` : '获取验证码'}
              </Button>
            } />
          </Form.Item>
          <Form.Item name="bio" label="个人简介">
            <TextArea rows={3} placeholder="介绍一下自己" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改密码 */}
      <Modal title="修改密码" open={pwdOpen} onCancel={() => setPwdOpen(false)} onOk={handleChangePassword} okText="确认修改" confirmLoading={loading} destroyOnClose>
        <Form form={passwordForm} layout="vertical">
          <Form.Item name="currentPassword" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="当前密码" />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '至少6位' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="新密码" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="确认新密码" dependencies={['newPassword']} rules={[{ required: true, message: '请确认新密码' }, ({ getFieldValue }) => ({ validator(_, v) { return (!v || getFieldValue('newPassword') === v) ? Promise.resolve() : Promise.reject(new Error('两次输入不一致')); } })]}>
            <Input.Password prefix={<LockOutlined />} placeholder="确认新密码" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 模块数据统计 */}
      <Modal title="📊 模块数据统计" open={statsOpen} onCancel={() => setStatsOpen(false)} footer={null} width={560}>
        <Row gutter={[16, 16]}>
          {Object.entries(statistics).sort(([, a], [, b]) => b.count - a.count).map(([key, stat]) => (
            <Col xs={12} sm={8} md={6} key={key}>
              <Statistic title={stat.name} value={stat.count} prefix={stat.icon} valueStyle={{ fontSize: 18 }} />
            </Col>
          ))}
        </Row>
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button type="link" icon={<ReloadOutlined />} onClick={fetchStatistics} loading={statsLoading}>刷新统计数据</Button>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
